import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const SelectorContainer = styled.div`
  position: relative;
`;

const SelectorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: #F4F6F6;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #EBEBFD;
  }
`;

const GlobeIcon = styled(Globe)`
  width: 1rem;
  height: 1rem;
  color: #0062FF;
`;

const LanguageText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #313133;
`;

const ChevronIcon = styled.svg`
  width: 1rem;
  height: 1rem;
  color: #8B9AB1;
  transition: transform 0.2s;
  ${props => props.$isOpen && "transform: rotate(180deg);"}
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  margin-top: 0.5rem;
  width: 12rem;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #EBEBFD;
  z-index: 50;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  text-align: left;
  border: none;
  background-color: ${props => props.$isActive ? "#F4F6F6" : "transparent"};
  color: ${props => props.$isActive ? "#0062FF" : "#313133"};
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #F4F6F6;
  }
`;

function LanguageSelector() {
    const { i18n, t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const languages = [
        { code: "en", name: t("languageSelector.english"), flag: "🇺🇸" },
        { code: "nl", name: t("languageSelector.dutch"), flag: "🇳🇱" },
    ];

    const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

    const changeLanguage = (langCode) => {
        i18n.changeLanguage(langCode);
        localStorage.setItem("language", langCode);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <SelectorContainer ref={dropdownRef}>
            <SelectorButton
                onClick={() => setIsOpen(!isOpen)}
                aria-label={t("languageSelector.selectLanguage")}
            >
                <GlobeIcon />
                <LanguageText>
                    {currentLanguage.name}
                </LanguageText>
                <ChevronIcon
                    $isOpen={isOpen}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                >
                    <polyline points="6 9 12 15 18 9" />
                </ChevronIcon>
            </SelectorButton>

            {isOpen && (
                <DropdownMenu>
                    {languages.map((lang) => (
                        <DropdownItem
                            key={lang.code}
                            onClick={() => changeLanguage(lang.code)}
                            $isActive={i18n.language === lang.code}
                        >
                            <span style={{ fontSize: '1.25rem' }}>{lang.flag}</span>
                            <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{lang.name}</span>
                            {i18n.language === lang.code && (
                                <svg
                                    style={{ marginLeft: 'auto', width: '1rem', height: '1rem', color: '#0062FF' }}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            )}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            )}
        </SelectorContainer>
    );
}

export default LanguageSelector;
